import React from "react";
import { motion } from "framer-motion";

import {
    FaWifi,
    FaSatelliteDish,
    FaLaptop,
    FaNetworkWired,
    FaGraduationCap,
    FaArrowRight,
    FaBolt,
    FaServer,
    FaTools,
    FaCheckCircle,
} from "react-icons/fa";

import campusHeaderImage from "./CampusPageImages/polugraph.jpg";
import internetInstallationImage from "./CampusPageImages/starlink.jpg";
import computerLabImage from "./CampusPageImages/computerlap.jpg";
import technologyTrainingImage from "./CampusPageImages/stafftraining.jpg";

import Footer from "./Footer";

const CampusPage = () => {
    const technologyProjects = [
        {
            id: 1,
            image: internetInstallationImage,
            icon: <FaSatelliteDish />,
            category: "Internet Infrastructure",
            title: "Internet & Starlink Installation",
            description:
                "Deploying reliable internet infrastructure for institutions and organizations, including Starlink-based connectivity, network setup, Wi-Fi deployment, and connectivity optimization.",
            features: [
                "Starlink Installation",
                "Wi-Fi Deployment",
                "Network Configuration",
                "Internet Infrastructure",
            ],
        },
        {
            id: 2,
            image: computerLabImage,
            icon: <FaLaptop />,
            category: "Computer Infrastructure",
            title: "Computer & Laptop Laboratory Setup",
            description:
                "Designing and setting up modern computer environments with properly configured computers, laptops, networking equipment, software, and essential digital infrastructure.",
            features: [
                "Computer Setup",
                "Laptop Configuration",
                "LAN Networking",
                "Software Installation",
            ],
        },
        {
            id: 3,
            image: technologyTrainingImage,
            icon: <FaGraduationCap />,
            category: "Technology Training",
            title: "Digital Skills & Technology Training",
            description:
                "Helping teams, institutions, students, and organizations develop practical technology skills through hands-on training and digital capacity-building programs.",
            features: [
                "Computer Training",
                "Digital Skills",
                "Software Training",
                "IT Capacity Building",
            ],
        },
    ];

    return (
        <div className="polu-campus-page">

            <style>{`

                /* =========================================================
                   GLOBAL
                ========================================================= */

                .polu-campus-page {
                    width: 100%;
                    min-height: 100vh;
                    overflow: hidden;
                    background: #f8fafc;
                    color: #111827;
                    font-family:
                        "Poppins",
                        "Inter",
                        sans-serif;
                }

                /* =========================================================
                   HERO
                ========================================================= */

                .polu-campus-hero {
                    position: relative;
                    min-height: 500px;
                    width: 100%;
                    overflow: hidden;
                    background: #0f172a;
                }

                .polu-campus-hero-image {
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    object-position: center;
                    opacity: 0.42;
                }

                .polu-campus-hero-overlay {
                    position: absolute;
                    inset: 0;
                    background:
                        linear-gradient(
                            115deg,
                            rgba(15, 23, 42, 0.96),
                            rgba(15, 23, 42, 0.72),
                            rgba(15, 23, 42, 0.46)
                        );
                }

                .polu-campus-grid {
                    position: absolute;
                    inset: 0;
                    opacity: 0.12;
                    background-image:
                        linear-gradient(
                            rgba(255,255,255,0.3) 1px,
                            transparent 1px
                        ),
                        linear-gradient(
                            90deg,
                            rgba(255,255,255,0.3) 1px,
                            transparent 1px
                        );
                    background-size: 65px 65px;
                    animation: poluGridMove 18s linear infinite;
                }

                @keyframes poluGridMove {

                    from {
                        transform: translate(0, 0);
                    }

                    to {
                        transform: translate(65px, 65px);
                    }

                }

                .polu-energy-orb {
                    position: absolute;
                    border-radius: 50%;
                    pointer-events: none;
                    filter: blur(2px);
                }

                .polu-energy-orb-one {
                    width: 350px;
                    height: 350px;
                    right: -120px;
                    top: -160px;
                    background:
                        radial-gradient(
                            circle,
                            rgba(249, 115, 22, 0.32),
                            transparent 70%
                        );
                    animation: poluFloatOne 7s ease-in-out infinite;
                }

                .polu-energy-orb-two {
                    width: 300px;
                    height: 300px;
                    left: -140px;
                    bottom: -160px;
                    background:
                        radial-gradient(
                            circle,
                            rgba(14, 165, 233, 0.25),
                            transparent 70%
                        );
                    animation: poluFloatTwo 8s ease-in-out infinite;
                }

                @keyframes poluFloatOne {

                    0%,
                    100% {
                        transform: translate(0, 0);
                    }

                    50% {
                        transform: translate(-40px, 35px);
                    }

                }

                @keyframes poluFloatTwo {

                    0%,
                    100% {
                        transform: translate(0, 0);
                    }

                    50% {
                        transform: translate(35px, -30px);
                    }

                }

                .polu-energy-line {
                    position: absolute;
                    width: 240px;
                    height: 1px;
                    background:
                        linear-gradient(
                            90deg,
                            transparent,
                            rgba(249, 115, 22, 0.8),
                            transparent
                        );
                    transform: rotate(-25deg);
                    animation: poluEnergyLine 5s linear infinite;
                }

                .polu-energy-line-one {
                    right: 8%;
                    top: 30%;
                }

                .polu-energy-line-two {
                    left: 10%;
                    top: 62%;
                    animation-delay: 2s;
                }

                @keyframes poluEnergyLine {

                    0% {
                        opacity: 0;
                        transform: translateX(-120px) rotate(-25deg);
                    }

                    30% {
                        opacity: 1;
                    }

                    70% {
                        opacity: 1;
                    }

                    100% {
                        opacity: 0;
                        transform: translateX(180px) rotate(-25deg);
                    }

                }

                .polu-campus-hero-content {
                    position: relative;
                    z-index: 5;
                    width: min(1250px, 90%);
                    min-height: 500px;
                    margin: auto;
                    display: flex;
                    align-items: center;
                    padding: 80px 0;
                }

                .polu-campus-hero-text {
                    max-width: 850px;
                }

                .polu-campus-hero-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 9px;
                    padding: 9px 15px;
                    margin-bottom: 20px;
                    border-radius: 30px;
                    border: 1px solid rgba(249, 115, 22, 0.4);
                    background: rgba(249, 115, 22, 0.1);
                    color: #fed7aa;
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 1.5px;
                    text-transform: uppercase;
                    backdrop-filter: blur(10px);
                }

                .polu-campus-hero-badge svg {
                    color: #fb923c;
                }

                .polu-campus-hero-text h1 {
                    margin: 0;
                    color: #ffffff;
                    font-size: clamp(
                        42px,
                        6vw,
                        76px
                    );
                    line-height: 1.02;
                    font-weight: 900;
                    letter-spacing: -2.5px;
                }

                .polu-campus-hero-text h1 span {
                    color: #f97316;
                }

                .polu-campus-hero-text p {
                    max-width: 700px;
                    margin: 25px 0 0;
                    color: #cbd5e1;
                    font-size: 16px;
                    line-height: 1.9;
                }

                .polu-campus-hero-bottom {
                    position: absolute;
                    z-index: 6;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    height: 6px;
                    display: flex;
                }

                .polu-campus-hero-bottom span:nth-child(1) {
                    flex: 2;
                    background: #f97316;
                }

                .polu-campus-hero-bottom span:nth-child(2) {
                    flex: 1;
                    background: #0ea5e9;
                }

                .polu-campus-hero-bottom span:nth-child(3) {
                    flex: 1;
                    background: #111827;
                }

                /* =========================================================
                   INTRO
                ========================================================= */

                .polu-tech-intro {
                    position: relative;
                    padding: 105px 5% 80px;
                    background:
                        radial-gradient(
                            circle at 5% 20%,
                            rgba(249, 115, 22, 0.07),
                            transparent 25%
                        ),
                        #ffffff;
                }

                .polu-tech-intro-container {
                    max-width: 1100px;
                    margin: auto;
                    text-align: center;
                }

                .polu-section-label {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    color: #f97316;
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 1.6px;
                    text-transform: uppercase;
                }

                .polu-tech-intro h2 {
                    margin: 13px 0 15px;
                    color: #111827;
                    font-size: clamp(
                        32px,
                        4vw,
                        48px
                    );
                    line-height: 1.12;
                    font-weight: 900;
                    letter-spacing: -1.5px;
                }

                .polu-tech-intro h2 span {
                    color: #f97316;
                }

                .polu-tech-intro p {
                    max-width: 760px;
                    margin: auto;
                    color: #64748b;
                    font-size: 14px;
                    line-height: 1.9;
                }

                /* =========================================================
                   PROJECT SECTION
                ========================================================= */

                .polu-projects-section {
                    position: relative;
                    padding: 30px 5% 110px;
                    background: #ffffff;
                }

                .polu-projects-container {
                    max-width: 1300px;
                    margin: auto;
                }

                .polu-projects-grid {
                    display: grid;
                    grid-template-columns:
                        repeat(3, minmax(0, 1fr));
                    gap: 25px;
                }

                .polu-project-card {
                    position: relative;
                    overflow: hidden;
                    border-radius: 24px;
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    box-shadow:
                        0 15px 40px rgba(15, 23, 42, 0.06);
                    transition:
                        transform 0.35s ease,
                        box-shadow 0.35s ease,
                        border-color 0.35s ease;
                }

                .polu-project-card:hover {
                    transform: translateY(-10px);
                    border-color: #fed7aa;
                    box-shadow:
                        0 25px 60px rgba(15, 23, 42, 0.12);
                }

                .polu-project-image {
                    position: relative;
                    height: 270px;
                    overflow: hidden;
                    background: #e2e8f0;
                }

                .polu-project-image img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition:
                        transform 0.6s ease;
                }

                .polu-project-card:hover
                .polu-project-image img {
                    transform: scale(1.07);
                }

                .polu-project-image::after {
                    content: "";
                    position: absolute;
                    inset: 0;
                    background:
                        linear-gradient(
                            180deg,
                            transparent 45%,
                            rgba(15, 23, 42, 0.45)
                        );
                    pointer-events: none;
                }

                .polu-project-number {
                    position: absolute;
                    z-index: 2;
                    top: 15px;
                    left: 15px;
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 12px;
                    background: rgba(17, 24, 39, 0.85);
                    color: #ffffff;
                    font-size: 12px;
                    font-weight: 800;
                    backdrop-filter: blur(8px);
                }

                .polu-project-icon {
                    position: absolute;
                    z-index: 2;
                    right: 15px;
                    top: 15px;
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 12px;
                    background: #f97316;
                    color: #ffffff;
                    box-shadow:
                        0 8px 20px rgba(249, 115, 22, 0.25);
                }

                .polu-project-content {
                    padding: 27px;
                }

                .polu-project-category {
                    display: block;
                    margin-bottom: 8px;
                    color: #f97316;
                    font-size: 10px;
                    font-weight: 800;
                    letter-spacing: 1.2px;
                    text-transform: uppercase;
                }

                .polu-project-content h3 {
                    margin: 0 0 12px;
                    color: #111827;
                    font-size: 21px;
                    line-height: 1.3;
                    font-weight: 850;
                }

                .polu-project-content p {
                    margin: 0 0 20px;
                    color: #64748b;
                    font-size: 13px;
                    line-height: 1.8;
                }

                .polu-project-features {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 7px;
                    margin-bottom: 22px;
                }

                .polu-project-feature {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    padding: 6px 8px;
                    border-radius: 7px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    color: #475569;
                    font-size: 9px;
                    font-weight: 600;
                }

                .polu-project-feature svg {
                    color: #f97316;
                    font-size: 8px;
                }

                .polu-project-link {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    color: #111827;
                    text-decoration: none;
                    font-size: 11px;
                    font-weight: 800;
                    transition:
                        color 0.2s ease,
                        gap 0.2s ease;
                }

                .polu-project-link svg {
                    color: #f97316;
                }

                .polu-project-link:hover {
                    color: #f97316;
                    gap: 12px;
                }

                /* =========================================================
                   PROCESS
                ========================================================= */

                .polu-process-section {
                    position: relative;
                    padding: 100px 5%;
                    background: #f8fafc;
                    overflow: hidden;
                }

                .polu-process-section::before {
                    content: "";
                    position: absolute;
                    inset: 0;
                    opacity: 0.35;
                    background-image:
                        radial-gradient(
                            #cbd5e1 1px,
                            transparent 1px
                        );
                    background-size: 25px 25px;
                }

                .polu-process-container {
                    position: relative;
                    z-index: 2;
                    max-width: 1200px;
                    margin: auto;
                }

                .polu-process-header {
                    text-align: center;
                    margin-bottom: 55px;
                }

                .polu-process-header h2 {
                    margin: 12px 0;
                    color: #111827;
                    font-size: clamp(
                        30px,
                        4vw,
                        44px
                    );
                    font-weight: 900;
                    letter-spacing: -1.3px;
                }

                .polu-process-header p {
                    max-width: 700px;
                    margin: auto;
                    color: #64748b;
                    font-size: 13px;
                    line-height: 1.8;
                }

                .polu-process-grid {
                    display: grid;
                    grid-template-columns:
                        repeat(4, 1fr);
                    gap: 18px;
                }

                .polu-process-card {
                    padding: 28px 22px;
                    text-align: center;
                    border-radius: 20px;
                    background: rgba(255,255,255,0.85);
                    border: 1px solid #e2e8f0;
                    backdrop-filter: blur(10px);
                }

                .polu-process-icon {
                    width: 48px;
                    height: 48px;
                    margin: 0 auto 17px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 14px;
                    color: #f97316;
                    background: #fff7ed;
                }

                .polu-process-card h3 {
                    margin: 0 0 8px;
                    color: #111827;
                    font-size: 15px;
                    font-weight: 800;
                }

                .polu-process-card p {
                    margin: 0;
                    color: #64748b;
                    font-size: 11px;
                    line-height: 1.7;
                }

                /* =========================================================
                   CTA
                ========================================================= */

                .polu-campus-cta {
                    max-width: 1250px;
                    margin: 90px auto;
                    padding: 55px;
                    position: relative;
                    overflow: hidden;
                    border-radius: 28px;
                    background:
                        linear-gradient(
                            120deg,
                            #111827,
                            #1e293b
                        );
                    box-shadow:
                        0 25px 60px rgba(15,23,42,0.13);
                }

                .polu-campus-cta::before {
                    content: "";
                    position: absolute;
                    width: 380px;
                    height: 380px;
                    right: -130px;
                    top: -190px;
                    border-radius: 50%;
                    background:
                        rgba(249,115,22,0.18);
                }

                .polu-campus-cta-content {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 30px;
                }

                .polu-campus-cta h2 {
                    margin: 0 0 10px;
                    color: #ffffff;
                    font-size: clamp(
                        25px,
                        4vw,
                        39px
                    );
                    font-weight: 900;
                }

                .polu-campus-cta p {
                    max-width: 700px;
                    margin: 0;
                    color: #cbd5e1;
                    font-size: 13px;
                    line-height: 1.8;
                }

                .polu-campus-cta-button {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    padding: 15px 21px;
                    border-radius: 11px;
                    background: #f97316;
                    color: #ffffff;
                    text-decoration: none;
                    font-size: 12px;
                    font-weight: 800;
                    white-space: nowrap;
                    transition:
                        transform 0.25s ease,
                        background 0.25s ease;
                }

                .polu-campus-cta-button:hover {
                    transform: translateY(-3px);
                    background: #ea580c;
                }

                /* =========================================================
                   RESPONSIVE
                ========================================================= */

                @media (max-width: 1050px) {

                    .polu-projects-grid {
                        grid-template-columns:
                            repeat(2, minmax(0, 1fr));
                    }

                    .polu-process-grid {
                        grid-template-columns:
                            repeat(2, 1fr);
                    }

                }

                @media (max-width: 750px) {

                    .polu-campus-hero {
                        min-height: 450px;
                    }

                    .polu-campus-hero-content {
                        min-height: 450px;
                    }

                    .polu-campus-hero-text h1 {
                        font-size: 45px;
                        letter-spacing: -1.5px;
                    }

                    .polu-campus-hero-text p {
                        font-size: 13px;
                    }

                    .polu-tech-intro {
                        padding: 75px 6% 60px;
                    }

                    .polu-projects-section {
                        padding: 20px 6% 75px;
                    }

                    .polu-projects-grid {
                        grid-template-columns: 1fr;
                    }

                    .polu-process-section {
                        padding: 75px 6%;
                    }

                    .polu-campus-cta {
                        margin: 70px 5%;
                        padding: 35px 25px;
                    }

                    .polu-campus-cta-content {
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .polu-campus-cta-button {
                        width: 100%;
                        justify-content: center;
                    }

                }

                @media (max-width: 480px) {

                    .polu-campus-hero-text h1 {
                        font-size: 37px;
                    }

                    .polu-project-image {
                        height: 230px;
                    }

                    .polu-project-content {
                        padding: 22px;
                    }

                    .polu-process-grid {
                        grid-template-columns: 1fr;
                    }

                    .polu-energy-line {
                        display: none;
                    }

                }

                @media (prefers-reduced-motion: reduce) {

                    .polu-campus-page *,
                    .polu-campus-page *::before,
                    .polu-campus-page *::after {
                        animation: none !important;
                        transition: none !important;
                    }

                }

            `}</style>

            {/* =========================================================
                HERO
            ========================================================= */}

            <section className="polu-campus-hero">

                <img
                    src={campusHeaderImage}
                    alt="POLU-Tech technology infrastructure"
                    className="polu-campus-hero-image"
                />

                <div className="polu-campus-hero-overlay"></div>

                <div className="polu-campus-grid"></div>

                <div className="polu-energy-orb polu-energy-orb-one"></div>

                <div className="polu-energy-orb polu-energy-orb-two"></div>

                <div className="polu-energy-line polu-energy-line-one"></div>

                <div className="polu-energy-line polu-energy-line-two"></div>

                <div className="polu-campus-hero-content">

                    <motion.div
                        className="polu-campus-hero-text"
                        initial={{
                            opacity: 0,
                            y: 40
                        }}
                        animate={{
                            opacity: 1,
                            y: 0
                        }}
                        transition={{
                            duration: 0.9
                        }}
                    >

                        <div className="polu-campus-hero-badge">

                            <FaBolt />

                            TECHNOLOGY IN ACTION

                        </div>

                        <h1>
                            We Build
                            <br />
                            <span>Technology That Connects.</span>
                        </h1>

                        <p>
                            From high-speed internet infrastructure and
                            computer laboratory deployments to hands-on
                            technology training, POLU-Tech helps organizations
                            build the digital infrastructure they need to
                            operate, learn, and grow.
                        </p>

                    </motion.div>

                </div>

                <div className="polu-campus-hero-bottom">

                    <span></span>
                    <span></span>
                    <span></span>

                </div>

            </section>

            {/* =========================================================
                INTRO
            ========================================================= */}

            <section className="polu-tech-intro">

                <div className="polu-tech-intro-container">

                    <div className="polu-section-label">

                        <FaNetworkWired />

                        OUR FIELD WORK

                    </div>

                    <h2>
                        Technology Beyond the
                        <span> Screen.</span>
                    </h2>

                    <p>
                        Our work is not limited to software and websites.
                        We also help organizations establish the physical
                        and technical infrastructure required to make
                        technology useful in the real world.
                    </p>

                </div>

            </section>

            {/* =========================================================
                PROJECTS
            ========================================================= */}

            <section className="polu-projects-section">

                <div className="polu-projects-container">

                    <div className="polu-projects-grid">

                        {technologyProjects.map(
                            (project, index) => (

                                <motion.article
                                    key={project.id}
                                    className="polu-project-card"
                                    initial={{
                                        opacity: 0,
                                        y: 50
                                    }}
                                    whileInView={{
                                        opacity: 1,
                                        y: 0
                                    }}
                                    transition={{
                                        duration: 0.65,
                                        delay: index * 0.12
                                    }}
                                    viewport={{
                                        once: true,
                                        amount: 0.15
                                    }}
                                >

                                    <div className="polu-project-image">

                                        <img
                                            src={project.image}
                                            alt={project.title}
                                        />

                                        <div className="polu-project-number">
                                            0{project.id}
                                        </div>

                                        <div className="polu-project-icon">
                                            {project.icon}
                                        </div>

                                    </div>

                                    <div className="polu-project-content">

                                        <span className="polu-project-category">
                                            {project.category}
                                        </span>

                                        <h3>
                                            {project.title}
                                        </h3>

                                        <p>
                                            {project.description}
                                        </p>

                                        <div className="polu-project-features">

                                            {project.features.map(
                                                (feature) => (

                                                    <span
                                                        key={feature}
                                                        className="polu-project-feature"
                                                    >
                                                        <FaCheckCircle />

                                                        {feature}

                                                    </span>

                                                )
                                            )}

                                        </div>

                                        <a
                                            href="#contact"
                                            className="polu-project-link"
                                        >
                                            Learn More

                                            <FaArrowRight />

                                        </a>

                                    </div>

                                </motion.article>

                            )
                        )}

                    </div>

                </div>

            </section>

            {/* =========================================================
                PROCESS
            ========================================================= */}

            <section className="polu-process-section">

                <div className="polu-process-container">

                    <div className="polu-process-header">

                        <div className="polu-section-label">

                            <FaTools />

                            HOW WE WORK

                        </div>

                        <h2>
                            From Planning to Deployment
                        </h2>

                        <p>
                            POLU-Tech approaches every technology
                            deployment with a practical process designed
                            around reliability, usability, and long-term
                            support.
                        </p>

                    </div>

                    <div className="polu-process-grid">

                        <div className="polu-process-card">

                            <div className="polu-process-icon">
                                <FaTools />
                            </div>

                            <h3>
                                01. Assessment
                            </h3>

                            <p>
                                We understand the organization's
                                infrastructure, requirements, and
                                technology goals.
                            </p>

                        </div>

                        <div className="polu-process-card">

                            <div className="polu-process-icon">
                                <FaNetworkWired />
                            </div>

                            <h3>
                                02. Planning
                            </h3>

                            <p>
                                We design the appropriate network,
                                hardware, software, and infrastructure
                                solution.
                            </p>

                        </div>

                        <div className="polu-process-card">

                            <div className="polu-process-icon">
                                <FaServer />
                            </div>

                            <h3>
                                03. Deployment
                            </h3>

                            <p>
                                Our team installs, configures, tests,
                                and prepares the technology environment.
                            </p>

                        </div>

                        <div className="polu-process-card">

                            <div className="polu-process-icon">
                                <FaGraduationCap />
                            </div>

                            <h3>
                                04. Training
                            </h3>

                            <p>
                                We train users and teams so they can
                                confidently operate and maintain their
                                technology.
                            </p>

                        </div>

                    </div>

                </div>

            </section>

            {/* =========================================================
                CTA
            ========================================================= */}

            <section className="polu-campus-cta">

                <div className="polu-campus-cta-content">

                    <div>

                        <h2>
                            Need Technology Infrastructure?
                        </h2>

                        <p>
                            POLU-Tech can help you plan, install,
                            configure, and train your team on the
                            technology infrastructure your organization
                            needs.
                        </p>

                    </div>

                    <a
                        href="#contact"
                        className="polu-campus-cta-button"
                    >
                        Work With Us

                        <FaArrowRight />

                    </a>

                </div>

            </section>

            <Footer />

        </div>
    );
};

export default CampusPage;